import { motion, useReducedMotion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  TrendingUp, Star, WifiOff, Flame, Award,
  CheckCircle, BookOpen, ChevronRight, Zap,
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { tr as dateFnsTr, enUS } from 'date-fns/locale';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useLanguage } from '../contexts/LanguageContext';
import { useCabinProgress } from '../hooks/cabin/useCabinProgress';
import { useCabinStats } from '../hooks/cabin/useCabinStats';
import { useBadges } from '../hooks/cabin/useBadges';
import { interpolate, getLevelFromMiles, getLevelProgress, LEVEL_TABLE } from '../utils/cabinI18n';
import CompletionRing from '../components/cabin/CompletionRing';
import StatCard from '../components/cabin/StatCard';
import WeeklyActivityChart from '../components/cabin/WeeklyActivityChart';

const pageVariants = {
  initial: { opacity: 0, y: 8 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -8 },
};

// Theme grouping for 16 modules (order-based)
const THEMES_CONFIG = [
  { id: 'foundations', orderRange: [1, 4] },
  { id: 'interview',   orderRange: [5, 10] },
  { id: 'sector',      orderRange: [11, 16] },
];

function getThemeId(order) {
  return (
    THEMES_CONFIG.find((th) => order >= th.orderRange[0] && order <= th.orderRange[1])?.id ||
    'sector'
  );
}

// ── Skeleton helpers ──────────────────────────────────────
function SkeletonLine({ w = 'w-1/2', h = 'h-3' }) {
  return <div className={`${h} ${w} bg-gray-200 rounded animate-pulse`} />;
}

function SkeletonModuleRow() {
  return (
    <div className="flex items-center gap-3 p-3 animate-pulse">
      <div className="w-10 h-10 bg-gray-200 rounded-xl shrink-0" />
      <div className="flex-1">
        <SkeletonLine w="w-2/3" h="h-3" />
        <div className="mt-1.5"><SkeletonLine w="w-full" h="h-2" /></div>
      </div>
      <SkeletonLine w="w-8" h="h-3" />
    </div>
  );
}

// ── Module row for Tab 2 ──────────────────────────────────
function ModuleProgressRow({ mod, navigate, lang, t, dateFnsLocale }) {
  const pct = mod.percent || 0;
  const statusColor = {
    completed: 'text-green-600',
    in_progress: 'text-[var(--brand-primary)]',
    not_started: 'text-gray-400',
  }[mod.status] || 'text-gray-400';

  const statusIcon = {
    completed: <CheckCircle size={16} className="text-green-500 shrink-0" aria-hidden="true" />,
    in_progress: <div className="w-4 h-4 rounded-full border-2 border-[var(--brand-primary)] shrink-0" />,
    not_started: <div className="w-4 h-4 rounded-full border-2 border-gray-300 shrink-0" />,
  }[mod.status] || null;

  const lastActive = mod.last_activity_at
    ? formatDistanceToNow(new Date(mod.last_activity_at), {
        addSuffix: true,
        locale: dateFnsLocale,
      })
    : null;

  const ctaLabel = mod.status === 'completed'
    ? (lang === 'tr' ? 'Tekrar Et' : 'Retry')
    : mod.status === 'in_progress'
    ? t('cabin.progress.go_to_module')
    : (lang === 'tr' ? 'Başla' : 'Start');

  return (
    <motion.div
      className="flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 transition-colors group cursor-pointer"
      onClick={() => navigate(`/programs/kabin-memurlugu/modules/${mod.slug}`)}
      role="button"
      tabIndex={0}
      aria-label={`${mod.title} — %${pct} tamamlandı`}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate(`/programs/kabin-memurlugu/modules/${mod.slug}`); } }}
      whileHover={{ x: 2 }}
      transition={{ type: 'spring', stiffness: 400, damping: 30 }}
    >
      {/* Status icon */}
      <div className="shrink-0">{statusIcon}</div>

      {/* Text + bar */}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-[var(--text-main)] truncate">{mod.title}</p>
        <div className="flex items-center gap-2 mt-1">
          <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-500 ${
                mod.status === 'completed'
                  ? 'bg-green-500'
                  : 'bg-[var(--brand-primary)]'
              }`}
              style={{ width: `${pct}%` }}
            />
          </div>
          <span className={`text-xs font-bold shrink-0 ${statusColor}`}>{pct}%</span>
        </div>
        <div className="flex items-center gap-2 mt-0.5">
          <span className="text-xs text-[var(--text-muted)]">
            {mod.completed_lessons}/{mod.total_lessons} {lang === 'tr' ? 'ders' : 'lessons'}
          </span>
          {lastActive && (
            <>
              <span className="text-gray-300">·</span>
              <span className="text-xs text-[var(--text-muted)]">{lastActive}</span>
            </>
          )}
        </div>
      </div>

      {/* CTA */}
      <span className="text-xs font-semibold text-[var(--brand-primary)] opacity-0 group-hover:opacity-100 transition-opacity shrink-0 flex items-center gap-0.5">
        {ctaLabel} <ChevronRight size={12} aria-hidden="true" />
      </span>
    </motion.div>
  );
}

// ── Main component ────────────────────────────────────────
export default function CabinProgress() {
  const { t, lang } = useLanguage();
  const navigate = useNavigate();
  const shouldReduce = useReducedMotion();
  const dateFnsLocale = lang === 'tr' ? dateFnsTr : enUS;

  const { data: progressData, isLoading: progressLoading, isError, refetch } = useCabinProgress();
  const { data: stats, isLoading: statsLoading } = useCabinStats();
  const { data: badges } = useBadges();

  const isLoading = progressLoading || statsLoading;

  // Merge stats data into progress summary
  const totalMiles = stats?.totalMiles || 0;
  const streak = stats?.streak || 0;
  const streakLongest = stats?.streakLongest || 0;
  const todayXp = stats?.todayXp || 0;
  const todayMiles = stats?.todayMiles || 0;
  const completedToday = stats?.completedLessons || 0;
  const weeklyActivity = stats?.weeklyActivity || [];

  const progressModules = progressData?.modules || [];
  const totalLessons = progressData?.totalLessons || 0;
  const completedLessons = progressData?.completedLessons || 0;
  const overallPct = totalLessons > 0 ? Math.round((completedLessons / totalLessons) * 100) : 0;

  const levelData = getLevelFromMiles(totalMiles);
  const levelPct = getLevelProgress(totalMiles);
  const nextLevel = LEVEL_TABLE.find((l) => l.order === levelData.order + 1);
  const milesToNext = nextLevel ? nextLevel.minMiles - totalMiles : 0;
  const isMaxLevel = !nextLevel;

  const unlockedBadges = Array.isArray(badges) ? badges.filter((b) => b.unlocked) : [];

  // Top 3 modules with highest progress (in_progress or partial)
  const topModules = [...progressModules]
    .filter((m) => m.percent > 0 && m.status !== 'not_started')
    .sort((a, b) => b.percent - a.percent)
    .slice(0, 3);

  // Theme grouping for Tab 3
  const themeGroups = THEMES_CONFIG.map((th) => {
    const thMods = progressModules.filter((m) => getThemeId(m.order) === th.id);
    const thCompleted = thMods.reduce((s, m) => s + (m.completed_lessons || 0), 0);
    const thTotal = thMods.reduce((s, m) => s + (m.total_lessons || 0), 0);
    const thPct = thTotal > 0 ? Math.round((thCompleted / thTotal) * 100) : 0;
    const thModDone = thMods.filter((m) => m.status === 'completed').length;
    return { ...th, modules: thMods, pct: thPct, modDone: thModDone };
  });

  const themeNames = {
    foundations: { tr: t('cabin.progress.theme.foundations'), en: t('cabin.progress.theme.foundations') },
    interview:   { tr: t('cabin.progress.theme.interview'),   en: t('cabin.progress.theme.interview') },
    sector:      { tr: t('cabin.progress.theme.sector'),      en: t('cabin.progress.theme.sector') },
  };

  const themeGradients = {
    foundations: 'from-blue-500 to-indigo-600',
    interview:   'from-purple-500 to-pink-600',
    sector:      'from-amber-500 to-orange-600',
  };

  if (isError && !isLoading) {
    return (
      <div className="p-6 flex flex-col items-center gap-4 min-h-[60vh] justify-center">
        <WifiOff size={48} className="text-[var(--brand-danger)]" aria-hidden="true" />
        <h2 className="text-lg font-black text-[var(--text-main)]">{t('cabin.error.title')}</h2>
        <p className="text-sm text-[var(--text-muted)]">{t('cabin.error.sub')}</p>
        <button
          onClick={refetch}
          className="px-5 py-2 bg-[var(--brand-primary)] text-white font-bold rounded-xl
                     shadow-[0_4px_0_0_var(--brand-primary-shadow)]
                     hover:bg-[var(--brand-primary-hover)] transition-colors
                     focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--brand-primary)]"
        >
          {t('cabin.error.cta')}
        </button>
      </div>
    );
  }

  return (
    <motion.div
      variants={shouldReduce ? {} : pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      transition={{ duration: 0.25, ease: 'easeOut' }}
      className="min-h-screen"
    >
      {/* ── Hero Header ── */}
      <div className="bg-gradient-to-r from-sky-600 via-blue-600 to-indigo-700 px-4 pt-6 pb-16 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto flex items-center gap-3">
          <TrendingUp size={28} className="text-white shrink-0" aria-hidden="true" />
          <div>
            <h1 className="text-2xl font-black text-white">{t('cabin.progress.title')}</h1>
            <p className="text-sm text-sky-200">{t('cabin.progress.subtitle')}</p>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-20 -mt-8 relative z-10">

        {/* ── Hero Card ── */}
        <div className="bg-white rounded-2xl border-2 border-gray-200 shadow-[0_4px_0_0_#E5E5E5] p-5 mb-6">
          {isLoading ? (
            <div className="animate-pulse flex gap-5 items-center">
              <div className="w-[120px] h-[120px] bg-gray-200 rounded-full shrink-0" />
              <div className="flex-1">
                <SkeletonLine w="w-1/3" h="h-4" />
                <div className="mt-2"><SkeletonLine w="w-1/2" h="h-3" /></div>
                <div className="mt-4 grid grid-cols-3 gap-2">
                  {[1, 2, 3].map((i) => <div key={i} className="h-12 bg-gray-200 rounded-xl" />)}
                </div>
              </div>
            </div>
          ) : (
            <div className="flex flex-col sm:flex-row items-center gap-6">
              {/* Completion ring */}
              <div className="shrink-0 flex flex-col items-center gap-1">
                <CompletionRing
                  progress={overallPct}
                  size={140}
                  strokeWidth={10}
                  label={`${overallPct}%`}
                  sublabel={t('cabin.progress.completion_ring.label')}
                />
              </div>

              {/* Stats */}
              <div className="flex-1 w-full min-w-0">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <span className="text-xl font-black text-[var(--text-main)]">{levelData.emoji} {t(`cabin.level.${levelData.key}`)}</span>
                  <span className="px-2 py-0.5 bg-sky-100 text-sky-700 rounded-lg text-xs font-bold">
                    {lang === 'tr' ? `Seviye ${levelData.order}` : `Level ${levelData.order}`}
                  </span>
                </div>

                <p className="text-sm text-[var(--text-muted)] mb-3">
                  {interpolate(t('cabin.progress.total_completed'), {
                    completed: completedLessons,
                    total: totalLessons,
                  })}
                </p>

                {/* Next Level progress bar — separate metric from lesson count */}
                <div className="mt-3 pt-3 border-t border-gray-100">
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-xs font-bold text-[var(--text-muted)] uppercase tracking-wide">
                      {t('cabin.progress.next_level')}
                    </span>
                    {!isMaxLevel && (
                      <span className="text-xs font-semibold text-sky-600">
                        {totalMiles} / {nextLevel?.minMiles}{' '}
                        {lang === 'tr' ? 'Mil' : 'Miles'}
                      </span>
                    )}
                  </div>
                  <div
                    className="h-2 bg-gray-100 rounded-full overflow-hidden"
                    role="progressbar"
                    aria-valuenow={levelPct}
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-label={interpolate(t('cabin.a11y.level_progress'), { pct: levelPct })}
                  >
                    <div
                      className="h-full bg-gradient-to-r from-sky-400 to-indigo-500 rounded-full transition-all duration-700"
                      style={{ width: `${levelPct}%` }}
                    />
                  </div>
                  <p className="text-xs text-[var(--text-muted)] mt-1">
                    {isMaxLevel
                      ? t('cabin.progress.level_card.max')
                      : interpolate(t('cabin.progress.next_level_miles'), { miles: milesToNext })
                    }
                  </p>
                </div>

                {/* Quick stats row */}
                <div className="grid grid-cols-3 gap-2">
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-2.5 text-center">
                    <div className="flex items-center justify-center gap-1 mb-0.5">
                      <Star size={14} className="text-amber-500" aria-hidden="true" />
                      <span className="text-sm font-black text-amber-700">
                        {new Intl.NumberFormat(lang === 'tr' ? 'tr-TR' : 'en-US').format(totalMiles)}
                      </span>
                    </div>
                    <span className="text-[10px] font-medium text-amber-600 leading-tight">
                      {t('cabin.progress.stats.total_miles')}
                    </span>
                  </div>
                  <div className="bg-orange-50 border border-orange-200 rounded-xl p-2.5 text-center">
                    <div className="flex items-center justify-center gap-1 mb-0.5">
                      <Flame size={14} className="text-orange-500" aria-hidden="true" />
                      <span className="text-sm font-black text-orange-700">{streak}</span>
                    </div>
                    <span className="text-[10px] font-medium text-orange-600 leading-tight">
                      {t('cabin.progress.stats.streak')}
                    </span>
                  </div>
                  <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-2.5 text-center">
                    <div className="flex items-center justify-center gap-1 mb-0.5">
                      <Award size={14} className="text-emerald-500" aria-hidden="true" />
                      <span className="text-sm font-black text-emerald-700">{unlockedBadges.length}</span>
                    </div>
                    <span className="text-[10px] font-medium text-emerald-600 leading-tight">
                      {lang === 'tr' ? 'Rozet' : 'Badges'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* ── Tabs ── */}
        <Tabs defaultValue="overview" aria-label={t('cabin.progress.tabs.aria_label')}>
          <TabsList className="mb-6 grid grid-cols-3 gap-1 w-full h-auto bg-white border-2 border-gray-200 rounded-xl p-1">
            {[
              { value: 'overview', label: t('cabin.progress.tab.overview') },
              { value: 'modules',  label: t('cabin.progress.tab.modules') },
              { value: 'themes',   label: t('cabin.progress.tab.themes') },
            ].map(({ value, label }) => (
              <TabsTrigger
                key={value}
                value={value}
                className="w-full text-xs sm:text-sm font-semibold rounded-lg py-2 px-1
                           data-[state=active]:bg-[var(--brand-primary)] data-[state=active]:text-white
                           transition-colors truncate min-h-[44px]"
                data-testid={`cabin-progress-tab-${value}`}
              >
                {label}
              </TabsTrigger>
            ))}
          </TabsList>

          {/* ── Tab 1 — Overview ── */}
          <TabsContent value="overview">
            {/* Today's summary */}
            <div className="bg-white rounded-2xl border-2 border-gray-200 shadow-[0_4px_0_0_#E5E5E5] p-5 mb-5">
              <h3 className="text-sm font-bold text-[var(--text-main)] mb-4 flex items-center gap-2">
                <Zap size={16} className="text-amber-500" aria-hidden="true" />
                {t('cabin.progress.today_summary')}
              </h3>
              {isLoading ? (
                <div className="grid grid-cols-3 gap-3 animate-pulse">
                  {[1, 2, 3].map((i) => <div key={i} className="h-16 bg-gray-200 rounded-xl" />)}
                </div>
              ) : (
                <div className="grid grid-cols-3 gap-3">
                  <StatCard
                    icon={<Zap size={18} strokeWidth={2.5} />}
                    value={todayXp}
                    label={t('cabin.progress.today.xp')}
                    testId="progress-today-xp"
                  />
                  <StatCard
                    icon={<Star size={18} strokeWidth={2.5} />}
                    value={todayMiles}
                    label={t('cabin.progress.today.miles')}
                    testId="progress-today-miles"
                  />
                  <StatCard
                    icon={<BookOpen size={18} strokeWidth={2.5} />}
                    value={completedToday}
                    label={t('cabin.progress.today.lessons')}
                    testId="progress-today-lessons"
                  />
                </div>
              )}
            </div>

            {/* Streak info */}
            {!isLoading && streak > 0 && (
              <div className="bg-orange-50 border-2 border-orange-200 rounded-2xl p-4 mb-5 flex items-center gap-3 shadow-[0_2px_0_0_#FED7AA]">
                <span className="text-3xl" aria-hidden="true">🔥</span>
                <div>
                  <p className="text-sm font-black text-orange-800">
                    {streak} {lang === 'tr' ? 'günlük seri!' : 'day streak!'}
                  </p>
                  <p className="text-xs text-orange-600">
                    {interpolate(t('cabin.progress.streak.longest'), {})} {streakLongest} {lang === 'tr' ? 'gün' : 'days'}
                  </p>
                </div>
              </div>
            )}

            {/* Weekly chart */}
            {isLoading ? (
              <div className="h-48 bg-gray-200 rounded-2xl animate-pulse mb-5" />
            ) : weeklyActivity.length > 0 ? (
              <div className="mb-5">
                <WeeklyActivityChart data={weeklyActivity} />
              </div>
            ) : null}

            {/* Top modules */}
            {!isLoading && topModules.length > 0 && (
              <div className="bg-white rounded-2xl border-2 border-gray-200 shadow-[0_4px_0_0_#E5E5E5] p-5">
                <h3 className="text-sm font-bold text-[var(--text-main)] mb-3 flex items-center gap-2">
                  <TrendingUp size={16} className="text-[var(--brand-primary)]" aria-hidden="true" />
                  {t('cabin.progress.top_modules')}
                </h3>
                <div className="flex flex-col divide-y divide-gray-100">
                  {topModules.map((mod) => (
                    <ModuleProgressRow
                      key={mod.id}
                      mod={mod}
                      navigate={navigate}
                      lang={lang}
                      t={t}
                      dateFnsLocale={dateFnsLocale}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Empty state */}
            {!isLoading && completedLessons === 0 && (
              <div className="mt-4 text-center py-10 px-4 bg-white rounded-2xl border-2 border-dashed border-gray-300">
                <p className="text-2xl mb-2" aria-hidden="true">🚀</p>
                <p className="text-sm font-semibold text-[var(--text-main)] mb-1">
                  {t('cabin.progress.empty')}
                </p>
                <button
                  onClick={() => navigate('/programs/kabin-memurlugu')}
                  className="mt-3 px-4 py-2 bg-[var(--brand-primary)] text-white text-xs font-bold rounded-xl
                             shadow-[0_4px_0_0_var(--brand-primary-shadow)]
                             hover:bg-[var(--brand-primary-hover)] transition-colors
                             focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--brand-primary)]"
                >
                  {lang === 'tr' ? 'Derse Başla' : 'Start a Lesson'}
                </button>
              </div>
            )}
          </TabsContent>

          {/* ── Tab 2 — Modules ── */}
          <TabsContent value="modules">
            {isLoading ? (
              <div className="bg-white rounded-2xl border-2 border-gray-200 shadow-[0_4px_0_0_#E5E5E5] overflow-hidden">
                {[1, 2, 3, 4, 5].map((i) => <SkeletonModuleRow key={i} />)}
              </div>
            ) : progressModules.length === 0 ? (
              <div className="text-center py-12 text-[var(--text-muted)]">
                <p className="text-sm">{t('cabin.progress.empty')}</p>
              </div>
            ) : (
              <div className="flex flex-col gap-4">
                {THEMES_CONFIG.map((th) => {
                  const thMods = progressModules
                    .filter((m) => getThemeId(m.order) === th.id)
                    .sort((a, b) => a.order - b.order);
                  if (thMods.length === 0) return null;

                  const name = lang === 'tr'
                    ? themeNames[th.id]?.tr
                    : themeNames[th.id]?.en;

                  return (
                    <div key={th.id}>
                      {/* Theme section heading */}
                      <div className="flex items-center gap-2 mb-2 px-1">
                        <div className={`w-3 h-3 rounded-full bg-gradient-to-br ${themeGradients[th.id]}`} aria-hidden="true" />
                        <h3 className="text-xs font-black text-[var(--text-muted)] uppercase tracking-wide">
                          {name}
                        </h3>
                      </div>

                      <div className="bg-white rounded-2xl border-2 border-gray-200 shadow-[0_4px_0_0_#E5E5E5] overflow-hidden divide-y divide-gray-100">
                        {thMods.map((mod) => (
                          <ModuleProgressRow
                            key={mod.id}
                            mod={mod}
                            navigate={navigate}
                            lang={lang}
                            t={t}
                            dateFnsLocale={dateFnsLocale}
                          />
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* ── Tab 3 — Themes ── */}
          <TabsContent value="themes">
            {isLoading ? (
              <div className="flex flex-col gap-4 animate-pulse">
                {[1, 2, 3].map((i) => <div key={i} className="h-28 bg-gray-200 rounded-2xl" />)}
              </div>
            ) : (
              <div className="flex flex-col gap-4">
                {themeGroups.map((th) => {
                  const name = lang === 'tr' ? themeNames[th.id]?.tr : themeNames[th.id]?.en;
                  const gradient = themeGradients[th.id];
                  const modsDoneLabel = interpolate(t('cabin.progress.theme.modules_done'), {
                    done: th.modDone,
                    total: th.modules.length,
                  });

                  return (
                    <div
                      key={th.id}
                      className="bg-white rounded-2xl border-2 border-gray-200 shadow-[0_4px_0_0_#E5E5E5] p-5"
                    >
                      <div className="flex items-center gap-4">
                        {/* Gradient icon */}
                        <div
                          className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${gradient} flex items-center justify-center shrink-0`}
                          aria-hidden="true"
                        >
                          <span className="text-xl">
                            {th.id === 'foundations' ? '🎯' : th.id === 'interview' ? '💼' : '✈️'}
                          </span>
                        </div>

                        {/* Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2 mb-1">
                            <h3 className="text-sm font-black text-[var(--text-main)]">{name}</h3>
                            <span className="text-xs font-bold text-[var(--text-muted)] shrink-0">
                              {th.pct}%
                            </span>
                          </div>
                          <p className="text-xs text-[var(--text-muted)] mb-2">{modsDoneLabel}</p>

                          {/* Theme progress bar */}
                          <div
                            className="h-2.5 bg-gray-100 rounded-full overflow-hidden"
                            role="progressbar"
                            aria-valuenow={th.pct}
                            aria-valuemin={0}
                            aria-valuemax={100}
                            aria-label={`${name} — ${th.pct}%`}
                          >
                            <div
                              className={`h-full rounded-full bg-gradient-to-r ${gradient} transition-all duration-700`}
                              style={{ width: `${th.pct}%` }}
                            />
                          </div>
                        </div>
                      </div>

                      {/* Lesson count row */}
                      <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-between text-xs text-[var(--text-muted)]">
                        <span>
                          {th.modules.reduce((s, m) => s + m.completed_lessons, 0)}/
                          {th.modules.reduce((s, m) => s + m.total_lessons, 0)}{' '}
                          {lang === 'tr' ? 'ders' : 'lessons'}
                        </span>
                        <button
                          onClick={() => navigate('/programs/kabin-memurlugu/roadmap')}
                          className="text-xs font-semibold text-[var(--brand-primary)] hover:underline
                                     focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--brand-primary)]"
                        >
                          {lang === 'tr' ? 'Yol haritasına git →' : 'View roadmap →'}
                        </button>
                      </div>
                    </div>
                  );
                })}

                {/* Overall summary footer */}
                <div className="bg-gradient-to-br from-sky-50 to-indigo-50 border-2 border-sky-200 rounded-2xl p-4 shadow-[0_2px_0_0_#BAE6FD]">
                  <p className="text-sm font-bold text-sky-800 mb-1">
                    {lang === 'tr' ? 'Genel Tamamlanma' : 'Overall Completion'}
                  </p>
                  <div className="flex items-center gap-3">
                    <div className="flex-1 h-3 bg-white rounded-full overflow-hidden border border-sky-200">
                      <div
                        className="h-full bg-gradient-to-r from-sky-400 to-indigo-500 rounded-full transition-all duration-700"
                        style={{ width: `${overallPct}%` }}
                      />
                    </div>
                    <span className="text-sm font-black text-sky-700 shrink-0">{overallPct}%</span>
                  </div>
                  <p className="text-xs text-sky-600 mt-1">
                    {interpolate(t('cabin.progress.total_completed'), {
                      completed: completedLessons,
                      total: totalLessons,
                    })}
                  </p>
                </div>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </motion.div>
  );
}
